import { Alert, Box, useTheme } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyIcon } from "../../../assets/images/icons/CopyIcon";
import BriaButton, { ButtonTypes } from "../BriaButton/BriaButton";
import styles from "./CopyToClipboardButton.module.scss";

export type CopyToClipboardButtonProps = {
	textToCopy?: string;
	className?: string;
	getTextToCopy?: () => Promise<string>;
	afterCopyAction?: () => void;
	buttonText?: string;
	tooltipText?: string;
	iconPosition?: "start" | "end";
	buttonType?: ButtonTypes;
	hideIcon?: boolean;
	loading?: boolean;
	disabled?: boolean;
};

const CopyToClipboardButton = ({
	textToCopy = "",
	className,
	getTextToCopy,
	afterCopyAction,
	buttonText,
	tooltipText,
	iconPosition = "start",
	buttonType = "textSmall",
	hideIcon = false,
	loading,
	disabled,
}: CopyToClipboardButtonProps) => {
	const { t } = useTranslation("translation");
	const [isSnackbarOpen, setSnackbarOpen] = useState(false);
	const theme = useTheme();

	const handleCopyToClipboard = async () => {
		if (getTextToCopy) {
			textToCopy = await getTextToCopy?.();
		}
		navigator.clipboard.writeText(textToCopy);
		setSnackbarOpen(true);
		afterCopyAction?.();
	};

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};

	return (
		<>
			<Box onClick={handleCopyToClipboard}>
				{!buttonText ? (
					<BriaButton
						buttonType="textSmall"
						className={className}
						title={tooltipText ?? t("Copy")}
						disabled={disabled}
					>
						<CopyIcon className={className} />
					</BriaButton>
				) : (
					<BriaButton
						className={clsx(className, styles.buttonText)}
						buttonType={buttonType}
						startIcon={
							!hideIcon &&
							iconPosition === "start" && (
								<CopyIcon
									strokeColor={buttonType === "primaryMedium" ? "#fff" : theme.palette.primary.light}
								/>
							)
						}
						endIcon={
							!hideIcon &&
							iconPosition === "end" && (
								<CopyIcon
									strokeColor={buttonType === "primaryMedium" ? "#fff" : theme.palette.primary.light}
								/>
							)
						}
						loading={loading}
						disabled={disabled}
					>
						{buttonText}
					</BriaButton>
				)}
			</Box>
			<Snackbar open={isSnackbarOpen} autoHideDuration={2000} onClose={handleCloseSnackbar}>
				<Alert onClose={handleCloseSnackbar} severity="success">
					{t("copyToClipboardSuccess")}
				</Alert>
			</Snackbar>
		</>
	);
};

export default CopyToClipboardButton;
